import { useEffect, useMemo } from "react";
import { useFormikContext } from 'formik';
import { usePopover } from "../../../hooks/usePopover"
import SearchSelectContent from "./content";
import { useCountryList, useStateList } from "./hooks";
import { isCountryField, isStateField, isSubField } from "./utils";
import styles from "./searchselect.module.css";
import textFieldStyles from '../TextField/TextField.module.css';
import { getLastItemOrBefore, getName, labelName, labelName1 } from "../../../utils";

/**
 * Search Select component for selecting options from a list with search functionality.
 * @param {Object} props 
 * @param {string} props.field_label
 * @param {Array} props.field_items
 * @param {string|null} props.parent
 * @param {boolean} props.isRequired
 * @param {string} props.field_key
 * @param {string} [props.initialValue]
 * @param {string} [props.linkedControllerName] set when this field is a dependent of another dropdown
 * @param {string[]} [props.linkedDependentNames] set when other dropdowns depend on this field
 */
export default function SearchSelect({ field_label, displayLabel, field_items, parent, isRequired, field_key, initialValue, linkedControllerName, linkedDependentNames}) {
  const { getFieldProps, setFieldValue, values } = useFormikContext();

  const popover = usePopover({ placement: 'bottom-start', closeOnEscape: true, closeOnScroll: false });

  // Field type
  const isSubFieldItem = isSubField(parent)
  const isCountryDropdown = isCountryField(field_label);
  const isStateDropdown = isStateField(field_label);
  const isLinkedDependent = !isCountryDropdown && !isStateDropdown && !!linkedControllerName;
  const isLinkedController = !!linkedDependentNames?.length;

  // Field name
  // Used to update field value using formik
  const fieldName = useMemo(() => {
    if (isCountryDropdown) return getName(parent, "country")
    if (isStateDropdown) return getName(parent, field_key || "state")
    return getLastItemOrBefore(getName(parent, field_label))
  }, [isCountryDropdown, isStateDropdown, getName, getLastItemOrBefore])


  // Countries query
  const { countries, loading: loadingCountries } = useCountryList({ enabled: isCountryDropdown });

  // If the current field is a country dropdown
  // siblingStateName is the name of the state field
  // that depends on this country field
  const siblingStateName = useMemo(() => {
    if (!isCountryDropdown) return ""
    return (
      getName(parent, field_key || "state") || 
      getLastItemOrBefore(getName(parent, field_label))
    )
  }, [isCountryDropdown, getName, getLastItemOrBefore, parent])

  // If the current field is a state dropdown
  // siblingCountry is the name and value of the country field
  // on which this state fields depends
  const siblingCountry = useMemo(() => {
    if (!isStateDropdown) {
      return { name: "", value: "" }
    }
    const name = 
      getName(parent, "country") || 
      getLastItemOrBefore(getName(parent, field_label))
    const value = getFieldProps(name).value;
    return { name, value }
  }, [isStateDropdown, getName, parent, getLastItemOrBefore, getFieldProps])

  // The token is extracted from sessionStorage
  // everytime the country value changes
  const siblingCountryToken = useMemo(() => {
    if (!siblingCountry.value) return "";
    return sessionStorage.getItem(siblingCountry.name) ?? "";
  }, [siblingCountry])

  const { states, loading: loadingStates } = useStateList(siblingCountryToken, {
    enabled: isStateDropdown
  });

  // If the current field depends on another dropdown (isLinkedDependent),
  // controllerValue is the currently selected value of that controlling field.
  const controllerFieldName = useMemo(() => {
    if (!isLinkedDependent) return ""
    return getLastItemOrBefore(getName(parent, linkedControllerName))
  }, [isLinkedDependent, getName, getLastItemOrBefore, parent, linkedControllerName])

  const controllerValue = isLinkedDependent ? getFieldProps(controllerFieldName).value : "";

  const isLoading = loadingCountries || loadingStates

  const options = useMemo(() => {
    if (isCountryDropdown) {
      return countries?.map((c) => ({ name: c.name, value: c.token })) ?? [];
    } else if (isStateDropdown) {
      return states?.map((s) => ({ name: s.name, value: s.name })) ?? []
    } else if (isLinkedDependent) {
      if (!controllerValue) return []
      return field_items
        .filter((i) => i.parent === controllerValue)
        .map((i) => ({ name: i.name, value: i.name }))
    } else if (field_items && Array.isArray(field_items)) {
      return field_items.map((i) => ({ name: i.name, value: i.name }))
    } else {
      return []
    }
  }, [isCountryDropdown, isStateDropdown, countries, states, field_items, isLinkedDependent, controllerValue])

  // Reset dependent fields' values whenever this field controls them
  // and its own value changes (e.g. changing category invalidates the
  // previously selected item from the old category's list).
  const resetLinkedDependents = () => {
    if (!isLinkedController) return;
    linkedDependentNames.forEach((depName) => {
      setFieldValue(getLastItemOrBefore(getName(parent, depName)), "");
    });
  }

  const handleOptionSelect = (option) => {
    const val = typeof option === "string" ? option : option.name;
    setFieldValue(fieldName, val);

    if (isCountryDropdown) {
      // Reset the dependent state field
      setFieldValue(siblingStateName, "");
      // save token to sessionStorage
      sessionStorage.setItem(fieldName, option.value)
    }
    resetLinkedDependents();
    popover.close();
  }

  const handleClear = () => {
    setFieldValue(fieldName, "");
    if (isCountryDropdown) {
      setFieldValue(siblingStateName, "");
      sessionStorage.removeItem(fieldName, )
    }
    resetLinkedDependents();
  }

  useEffect(() => {
    // If there is an initial country value
    // save it to session storage
    if (!isCountryDropdown || !initialValue || !countries) return;
    const option = countries.find((c) => c.name === initialValue);
    if (!option) return;
    sessionStorage.setItem(fieldName, option.token)
  }, [isCountryDropdown, initialValue, countries]);

  return (
    <div className={isSubFieldItem ? styles.container : textFieldStyles.inputContainer}>
      <label className={textFieldStyles.label}>
        {labelName(displayLabel || field_label)}
        {isRequired == 'true' && <span style={{ color: '#d32f2f', padding: 0 }}>*</span>}
      </label>
      <div>
        <div ref={popover.triggerRef} className={styles.inputValueContainer}>
          {isLoading && (
            <svg fill="#616161" width={20} viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"> 
              <path d="M8,1V2.8A5.2,5.2,0,1,1,2.8,8H1A7,7,0,1,0,8,1Z" />
            </svg>
          )}
          <input
            className={styles.inputValue}
            data-is-subfield={isSubFieldItem}
            data-is-loading={isLoading}
            data-has-clear={!!getFieldProps(fieldName).value}
            disabled={isLoading || (isLinkedDependent && !controllerValue)}
            placeholder={
              isLinkedDependent && !controllerValue
                ? "Select the previous option first"
                : `Select - ${labelName1(displayLabel || field_label)}`
            }
            readOnly={true}
            onFocus={popover.open}
            {...getFieldProps(fieldName)}
          />

          {isRequired != "true" && getFieldProps(fieldName).value && (
           <button type="button" data-clear onClick={handleClear}>
            <svg fill="#616161" width={10} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 492 492" xmlSpace="preserve"
            >
              <path d="M300.188,246L484.14,62.04c5.06-5.064,7.852-11.82,7.86-19.024c0-7.208-2.792-13.972-7.86-19.028L468.02,7.872 c-5.068-5.076-11.824-7.856-19.036-7.856c-7.2,0-13.956,2.78-19.024,7.856L246.008,191.82L62.048,7.872 c-5.06-5.076-11.82-7.856-19.028-7.856c-7.2,0-13.96,2.78-19.02,7.856L7.872,23.988c-10.496,10.496-10.496,27.568,0,38.052 L191.828,246L7.872,429.952c-5.064,5.072-7.852,11.828-7.852,19.032c0,7.204,2.788,13.96,7.852,19.028l16.124,16.116 c5.06,5.072,11.824,7.856,19.02,7.856c7.208,0,13.968-2.784,19.028-7.856l183.96-183.952l183.952,183.952 c5.068,5.072,11.824,7.856,19.024,7.856h0.008c7.204,0,13.96-2.784,19.028-7.856l16.12-16.116 c5.06-5.064,7.852-11.824,7.852-19.028c0-7.204-2.792-13.96-7.852-19.028L300.188,246z" />
            </svg>
           </button>
          )}
        </div>

        {popover.isOpen && (
          <SearchSelectContent 
            ref={popover.popoverRef} 
            value={getFieldProps(fieldName).value}
            data={options}
            top={popover.position.top}
            left={popover.position.left} 
            width={popover.triggerWidth}
            placement={popover.placement}
            positioned={popover.positioned}
            onSelect={handleOptionSelect}
          />
        )}
      </div>
    </div>
  )
}